import type { TemplateSourceTag, TemplateSummary } from '@/types/templates';

export interface TemplateCatalogFilters {
  search: string;
  tags: TemplateSourceTag[];
}

export const TEMPLATE_TAG_ORDER: TemplateSourceTag[] = [
  'marketplace',
  'classifieds',
  'auto',
  'finance',
  'news',
  'jobs',
  'real-estate',
  'travel',
  'retail',
  'social',
  'video',
  'search',
  'lead-generation',
  'contact-enrichment',
  'ads',
];

export const TEMPLATE_TAG_LABELS: Record<TemplateSourceTag, string> = {
  marketplace: 'Маркетплейсы',
  classifieds: 'Объявления',
  auto: 'Авто',
  finance: 'Финансы',
  news: 'Новости',
  jobs: 'Вакансии',
  'real-estate': 'Недвижимость',
  travel: 'Путешествия',
  retail: 'Ритейл',
  social: 'Соцсети',
  video: 'Видео',
  search: 'Поиск',
  'lead-generation': 'Лидогенерация',
  'contact-enrichment': 'Обогащение контактов',
  ads: 'Реклама',
};

const TEMPLATE_TAG_SET = new Set<TemplateSourceTag>(TEMPLATE_TAG_ORDER);
const MAX_SEARCH_LENGTH = 120;

type QueryValue = string | string[] | undefined;
type QueryRecord = Record<string, QueryValue>;
type QueryReader = {
  get: (name: string) => string | null;
  getAll: (name: string) => string[];
};

export type TemplateCatalogQuery = QueryRecord | QueryReader | undefined;

function isQueryReader(value: TemplateCatalogQuery): value is QueryReader {
  return Boolean(value) && typeof value.get === 'function' && typeof value.getAll === 'function';
}

export function isTemplateSourceTag(value: string): value is TemplateSourceTag {
  return TEMPLATE_TAG_SET.has(value as TemplateSourceTag);
}

export function normalizeSearchTerm(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_SEARCH_LENGTH);
}

function normalizeTags(values: string[]): TemplateSourceTag[] {
  const selected = new Set<TemplateSourceTag>();
  values.forEach((value) => {
    if (isTemplateSourceTag(value)) {
      selected.add(value);
    }
  });
  return TEMPLATE_TAG_ORDER.filter((tag) => selected.has(tag));
}

function readQueryValue(query: TemplateCatalogQuery, key: string): string {
  if (!query) {
    return '';
  }
  if (isQueryReader(query)) {
    return query.get(key) ?? '';
  }
  const raw = query[key];
  if (Array.isArray(raw)) {
    return raw.find((value) => typeof value === 'string') ?? '';
  }
  return typeof raw === 'string' ? raw : '';
}

function readQueryValues(query: TemplateCatalogQuery, key: string): string[] {
  if (!query) {
    return [];
  }
  if (isQueryReader(query)) {
    return query.getAll(key).filter((value) => value.length > 0);
  }
  const raw = query[key];
  if (Array.isArray(raw)) {
    return raw.filter((value): value is string => typeof value === 'string' && value.length > 0);
  }
  return typeof raw === 'string' && raw.length > 0 ? [raw] : [];
}

export function parseCatalogFiltersFromQuery(query: TemplateCatalogQuery): TemplateCatalogFilters {
  return {
    search: normalizeSearchTerm(readQueryValue(query, 'q')),
    tags: normalizeTags(readQueryValues(query, 'tag')),
  };
}

export function buildCatalogQuery(filters: TemplateCatalogFilters): URLSearchParams {
  const query = new URLSearchParams();
  const search = normalizeSearchTerm(filters.search);
  const tags = normalizeTags(filters.tags);
  if (search) {
    query.set('q', search);
  }
  tags.forEach((tag) => query.append('tag', tag));
  return query;
}

export function filterTemplates(
  templates: TemplateSummary[],
  filters: TemplateCatalogFilters,
): TemplateSummary[] {
  const search = normalizeSearchTerm(filters.search).toLowerCase();
  const selectedTags = new Set(normalizeTags(filters.tags));

  return templates.filter((template) => {
    if (selectedTags.size > 0 && !template.tags.some((tag) => selectedTags.has(tag))) {
      return false;
    }

    if (!search) {
      return true;
    }

    const haystack = [
      template.name,
      template.description,
      template.sourceDomains.join(' '),
      template.tags.join(' '),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(search);
  });
}
